import { useState, useEffect, useRef } from 'react'
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  Unsubscribe,
  QuerySnapshot,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { signInWithCustomToken } from 'firebase/auth'
import { auth, db } from '../config/firebase'
import {
  getKyousouApiClient,
  ApiErrorResponse,
  handleError,
} from '../services/network'
import { useSnackBar } from '../providers/snack_bar_provider'
import {
  FirestoreMessage,
  FirestoreSystemMessage,
  FirestoreUserMessage,
  AttachmentInfo,
} from '../types/chat'

interface UseFirestoreChatResult {
  messages: FirestoreMessage[]
  loading: boolean
  error: string | null
  sendMessage: (
    content: string,
    attachments?: AttachmentInfo[]
  ) => Promise<void>
  deleteMessage: (messageId: string) => Promise<void>
  isAuthenticated: boolean
}

export const useFirestoreChat = (
  chatRoomId: number,
  firebaseRoomId: string,
  firebaseToken: string | null,
  currentUserId: number,
  maxMessages = 10,
  onNewMessage?: () => void
): UseFirestoreChatResult => {
  const [messages, setMessages] = useState<FirestoreMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const unsubscribeRef = useRef<Unsubscribe | null>(null)
  const pendingMessagesRef = useRef<Map<string, number>>(new Map()) // tempId -> timestamp
  const { showSnackBar } = useSnackBar()
  const apiClient = getKyousouApiClient()

  // Firebase認証とリスナー設定
  useEffect(() => {
    const authenticateAndListen = async () => {
      try {
        if (!firebaseToken) {
          return
        }

        setLoading(true)

        // Firebase認証
        await signInWithCustomToken(auth, firebaseToken)
        setIsAuthenticated(true)

        // メッセージリスナー設定
        const messagesRef = collection(db, 'chats', firebaseRoomId, 'messages')
        const messagesQuery = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          limit(maxMessages)
        )

        const unsubscribe = onSnapshot(
          messagesQuery,
          (snapshot: QuerySnapshot) => {
            const messagesList: FirestoreMessage[] = []

            snapshot.forEach((doc: QueryDocumentSnapshot) => {
              const data = doc.data()

              if (data.chatType === 'user') {
                messagesList.push({
                  id: doc.id,
                  chatType: 'user',
                  senderEnterpriseId: data.senderEnterpriseId,
                  content: data.content,
                  attachments: data.attachments || [],
                  createdAt: data.createdAt.toDate(),
                  isEdited: data.isEdited || false,
                } as FirestoreUserMessage)
              } else {
                messagesList.push({
                  id: doc.id,
                  chatType: 'system',
                  messageType: data.messageType,
                  targetEnterpriseId: data.targetEnterpriseId,
                  matchingRequestId: data.matchingRequestId,
                  createdAt: data.createdAt.toDate(),
                } as FirestoreSystemMessage)
              }
            })

            // 効率的な重複除去：実際のメッセージで一時メッセージを置換
            setMessages((prev) => {
              // Firestoreクエリは降順なので昇順に並べ直す
              const realMessages = messagesList
                .filter(
                  (msg) =>
                    msg.chatType === 'user' ||
                    (msg.chatType === 'system' &&
                      msg.targetEnterpriseId === currentUserId)
                )
                .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())

              // 現在の一時メッセージ（pending）のみを抽出
              const pendingMessages = prev.filter(
                (msg) => msg.chatType === 'user' && msg.isPending
              )

              // 実際のメッセージと一致する一時メッセージを特定
              const matchedTempIds = new Set<string>()

              for (const realMsg of realMessages) {
                // 送信から10秒以内の一時メッセージを検索
                const matchedTemp = pendingMessages.find(
                  (tempMsg) =>
                    tempMsg.chatType === 'user' &&
                    realMsg.chatType === 'user' &&
                    tempMsg.senderEnterpriseId === realMsg.senderEnterpriseId &&
                    tempMsg.content === realMsg.content &&
                    Math.abs(
                      realMsg.createdAt.getTime() - tempMsg.createdAt.getTime()
                    ) < 10000 &&
                    // 添付ファイルの数が一致するか
                    (tempMsg.attachments || []).length ===
                      (realMsg.attachments || []).length &&
                    // 各添付ファイルの内容が順番通りに一致するか
                    (tempMsg.attachments || []).every((attachment, index) => {
                      const realAttachment = (realMsg.attachments || [])[index]
                      return (
                        attachment.url === realAttachment.url &&
                        attachment.fileName === realAttachment.fileName
                      )
                    })
                )

                if (matchedTemp) {
                  matchedTempIds.add(matchedTemp.id)
                  // pending messagesから削除
                  pendingMessagesRef.current.delete(matchedTemp.id)
                }
              }

              // 一致しなかった一時メッセージを保持
              const remainingPendingMessages = pendingMessages.filter(
                (msg) => !matchedTempIds.has(msg.id)
              )

              // 実際のメッセージ + 残りの一時メッセージを時系列順にマージ
              return [...realMessages, ...remainingPendingMessages].sort(
                (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
              )
            })

            // 新しいメッセージが自分の送信したものかチェック
            const newMessages = snapshot
              .docChanges()
              .filter((change) => change.type === 'added')
            const hasNewMessagesFromOthers = newMessages.some((change) => {
              const data = change.doc.data()
              return (
                data.chatType === 'user' &&
                data.senderEnterpriseId !== currentUserId
              )
            })

            const isPageVisible = document.visibilityState === 'visible'
            // 他のユーザーからの新しいメッセージがある場合のみ既読処理
            if (hasNewMessagesFromOthers && isPageVisible && onNewMessage) {
              onNewMessage()
            }
          },
          (err: unknown) => {
            console.error('Firestore listener error:', err)
            setError('メッセージの監視でエラーが発生しました')
            // エラー時はリスナーを停止してリトライしない
            if (unsubscribeRef.current) {
              unsubscribeRef.current()
              unsubscribeRef.current = null
            }
          }
        )

        unsubscribeRef.current = unsubscribe
      } catch (err) {
        console.error('Firebase authentication error:', err)
        setError(
          err instanceof Error ? err.message : 'Firebase認証に失敗しました'
        )
        setIsAuthenticated(false)
      } finally {
        setLoading(false)
      }
    }

    authenticateAndListen()

    // クリーンアップ
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
      // pending messagesもクリア
      pendingMessagesRef.current.clear()
    }
  }, [chatRoomId, firebaseRoomId, firebaseToken])

  // メッセージ送信 (API経由)
  const sendMessage = async (
    content: string,
    attachments: AttachmentInfo[] = []
  ) => {
    if (!chatRoomId || !isAuthenticated) {
      throw new Error('チャットルームが選択されていないか、認証されていません')
    }

    // 楽観的更新用の一時メッセージ（タイムスタンプベースID）
    const timestamp = Date.now()
    const tempId = `temp_${timestamp}_${Math.random().toString(36).slice(2)}`
    const optimisticMessage: FirestoreUserMessage = {
      id: tempId,
      chatType: 'user',
      senderEnterpriseId: currentUserId,
      content,
      attachments,
      createdAt: new Date(timestamp),
      isPending: true,
    }

    // pending messagesを記録
    pendingMessagesRef.current.set(tempId, timestamp)

    // UIを即座に更新
    setMessages((prev) => [...prev, optimisticMessage])

    try {
      // APIを通じてメッセージを送信
      const response = await apiClient.chat
        .roomsMessagesCreate(chatRoomId, {
          content,
          attachments,
        })
        .catch((e: ApiErrorResponse) => {
          // エラー時は楽観的更新を取り消し
          setMessages((prev) => prev.filter((msg) => msg.id !== tempId))
          pendingMessagesRef.current.delete(tempId)
          handleError(e, showSnackBar)
          throw new Error('メッセージの送信に失敗しました')
        })

      if (!response) {
        // エラー時は楽観的更新を取り消し
        setMessages((prev) => prev.filter((msg) => msg.id !== tempId))
        pendingMessagesRef.current.delete(tempId)
        throw new Error('メッセージの送信に失敗しました')
      }

      // 成功時は一時メッセージをそのまま残す（Firestoreリスナーが効率的に置換する）
    } catch (err) {
      console.error('Message send error:', err)
      throw err
    }
  }

  // メッセージ削除
  const deleteMessage = async (messageId: string) => {
    if (!chatRoomId || !isAuthenticated) {
      throw new Error('チャットルームが選択されていないか、認証されていません')
    }

    // 元のメッセージを保存（ロールバック用）
    const originalMessage = messages.find((msg) => msg.id === messageId)
    if (!originalMessage) {
      throw new Error('削除対象のメッセージが見つかりません')
    }

    // 楽観的更新: メッセージをリストから即座に削除
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId))

    try {
      const response = await apiClient.chat
        .roomsMessagesDelete(chatRoomId, messageId)
        .catch((e: ApiErrorResponse) => {
          // エラー時はメッセージを復活させる
          setMessages((prev) => {
            // 元の位置に挿入（時系列順を維持）
            const result = [...prev, originalMessage].sort(
              (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
            )
            return result
          })
          handleError(e, showSnackBar)
          throw new Error('メッセージの削除に失敗しました')
        })

      if (!response) {
        // エラー時はメッセージを復活させる
        setMessages((prev) => {
          // 元の位置に挿入（時系列順を維持）
          const result = [...prev, originalMessage].sort(
            (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
          )
          return result
        })
        throw new Error('メッセージの削除に失敗しました')
      }
    } catch (err) {
      console.error('Message delete error:', err)
      throw err
    }
  }

  return {
    messages,
    loading,
    error,
    sendMessage,
    deleteMessage,
    isAuthenticated,
  }
}
